import { Blocks } from "@/components/cms/Blocks";
import { PageSections, type SectionProps, type SectionRenderers } from "@/components/cms/PageSections";
import { linkSlot, visibleItems } from "@/components/cms/SectionRenderer";
import { ArrowRight, ButtonLink } from "@/components/ui/Button";
import { ImpactCounter } from "@/components/ui/ImpactCounter";
import { OutcomeFilter } from "@/components/ui/OutcomeFilter";
import { PageHeader } from "@/components/ui/PageHeader";
import { Reveal, RevealGroup } from "@/components/ui/Reveal";
import { Section, SectionHeading } from "@/components/ui/Section";
import { useContent } from "@/content-context";
import { outcomeRecords } from "@/data/impact";
import { buildMetadata } from "@/lib/seo";
import { pageLoader } from "@/page-data";

export const loader = pageLoader("impact");

export const meta = () =>
  buildMetadata({
    title: "Impact",
    description:
      "Documented HISCUS results, each with the period it refers to: the Sarangkot Nestling Home, restored water access in Bethanchowk, and dated outcomes by pillar and year.",
    path: "/impact",
  });

/** The headline figures, from the Impact figures collection, each with its source. */
function HeadlineSection({ section }: SectionProps) {
  const { impact } = useContent();
  if (!impact.length) return null;
  return (
    <Section tone="navy" ariaLabelledBy="headline-metrics-heading">
      <SectionHeading
        id="headline-metrics-heading"
        eyebrow={section.eyebrow}
        title={section.title || "Headline figures"}
        tone="light"
      />

      <RevealGroup as="ul" className="mt-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
        {impact.map((metric) => (
          <Reveal as="li" key={metric.key}>
            {/*
              tone="light" is not optional here. This Section is navy, and
              without it the counter falls back to its dark tone and renders
              near-black figures and grey labels on a dark blue ground.
            */}
            <ImpactCounter
              value={metric.value}
              suffix={metric.suffix ?? undefined}
              label={metric.label}
              context={metric.context}
              asOf={metric.asOf}
              tone="light"
            />
            {metric.source ? (
              <a
                href={metric.source}
                target="_blank"
                rel="noreferrer noopener"
                className="mt-3 inline-flex min-h-11 items-center text-[0.8125rem] text-sky underline underline-offset-4"
              >
                Source
              </a>
            ) : null}
          </Reveal>
        ))}
      </RevealGroup>
    </Section>
  );
}

function RecordSection({ section }: SectionProps) {
  return (
    <Section ariaLabelledBy="record-heading">
      <SectionHeading
        id="record-heading"
        eyebrow={section.eyebrow}
        title={section.title || "Documented outcomes by programme and year"}
        lead={section.lead}
      />
      <div className="mt-10">
        <OutcomeFilter records={outcomeRecords} />
      </div>
    </Section>
  );
}

function MethodSection({ section }: SectionProps) {
  const legacy = visibleItems(section);
  const secondary = linkSlot(section, 0);
  const primary = linkSlot(section, 1);
  const texts = section.texts ?? {};

  return (
    <Section tone="ivory" ariaLabelledBy="method-heading">
      <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
        <Reveal>
          <SectionHeading id="method-heading" eyebrow={section.eyebrow} title={section.title || "How we report"} />
          <Blocks blocks={section.body} className="mt-6" />
          {legacy.length ? (
            <ul className="mt-5 space-y-2">
              {legacy.map((figure) => (
                <li key={figure.title} className="flex gap-3 text-[0.9375rem] text-ink">
                  <span aria-hidden="true" className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-line-strong" />
                  {figure.title}
                </li>
              ))}
            </ul>
          ) : null}
        </Reveal>

        {texts.panelTitle || texts.panelBody ? (
          <Reveal>
            <div className="rounded-[16px] border border-line bg-white p-6 md:p-8">
              {texts.panelTitle ? <h2 style={{ fontSize: "var(--step-h3)" }}>{texts.panelTitle}</h2> : null}
              {texts.panelBody ? <p className="mt-4 text-muted">{texts.panelBody}</p> : null}
              {texts.panelNote ? <p className="mt-4 text-muted">{texts.panelNote}</p> : null}
              {secondary || primary ? (
                <div className="mt-7 flex flex-wrap gap-3">
                  {secondary ? (
                    <ButtonLink href={secondary.href} variant="secondary">
                      {secondary.label}
                    </ButtonLink>
                  ) : null}
                  {primary ? (
                    <ButtonLink href={primary.href}>
                      {primary.label}
                      <ArrowRight />
                    </ButtonLink>
                  ) : null}
                </div>
              ) : null}
            </div>
          </Reveal>
        ) : null}
      </div>
    </Section>
  );
}

export const impactSections: SectionRenderers = {
  "impact.headline": HeadlineSection,
  "impact.record": RecordSection,
  "impact.method": MethodSection,
};

export function ImpactPage() {
  return (
    <>
      <PageHeader
        eyebrow="Evidence"
        title="What HISCUS can evidence"
        lead="Every figure on this page carries the period it refers to. Where a reporting period is not yet confirmed, that is stated instead of being rounded away."
        trail={[
          { name: "Home", href: "/" },
          { name: "Impact", href: "/impact" },
        ]}
      />
      <PageSections renderers={impactSections} />
    </>
  );
}
